import React, { useEffect } from 'react';
import { Handle, Position, useUpdateNodeInternals, NodeProps } from 'reactflow';
import { Box, Typography } from '@mui/material';
import CallSplitIcon from '@mui/icons-material/CallSplit';

interface ConditionalRouteNodeData {
  label: string;
  condition?: string;
}

const ConditionalRouteNode: React.FC<NodeProps<ConditionalRouteNodeData>> = ({ 
  data = { label: 'Conditional Route' },
  id
}) => {
  const updateNodeInternals = useUpdateNodeInternals();

  useEffect(() => {
    if (id) {
      updateNodeInternals(id);
    }
  }, [id, updateNodeInternals]);

  return (
    <Box
      sx={{
        width: 140,
        height: 40,
        backgroundColor: '#ffffff',
        borderRadius: '8px',
        display: 'flex',
        alignItems: 'center',
        boxShadow: '0px 2px 8px rgba(0, 0, 0, 0.1)',
        border: '1px solid #80BFFF',
        px: 1,
        outline: 'none',
        '&:focus': {
          outline: 'none',
        },
        '&:hover': {
          outline: 'none',
        },
      }}
    >
      <Box
        sx={{
          width: 18,
          height: 18,
          backgroundColor: '#3B82F6',
          borderRadius: '6px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          mr: 1,
        }}
      >
        <CallSplitIcon sx={{ color: '#ffffff', fontSize: 10 }} />
      </Box>
      <Box sx={{ flex: 1 }}>
        <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '9px', lineHeight: 1.2, color: '#1F2937' }}>
          {data.label}
        </Typography>
        {data.condition && (
          <Typography variant="caption" sx={{ fontSize: '7px', color: '#6B7280' }}>
            ✨ {data.condition.substring(0, 20)}...
          </Typography>
        )}
      </Box>
      
      {/* Input handle */}
      <Handle
        type="target"
        position={Position.Left}
        style={{ background: '#000000', width: 5, height: 5 }}
      />
      
      {/* True handle (top right) */}
      <Handle
        type="source"
        position={Position.Right}
        id="true"
        style={{ 
          background: '#000000',
          width: 5,
          height: 5,
          top: '25%',
        }}
      />
      
      {/* False handle (bottom right) - SOURCE handle for outgoing connections */}
      <Handle
        type="source"
        position={Position.Right}
        id="false"
        style={{ 
          background: '#000000',
          width: 5,
          height: 5,
          top: '75%',
        }}
      />
      
      <Typography
        variant="caption"
        sx={{
          position: 'absolute',
          right: 6,
          top: '18%',
          fontSize: '5px',
          color: '#6B7280',
          fontWeight: 500,
          whiteSpace: 'nowrap',
          pointerEvents: 'none',
        }}
      >
        True
      </Typography>
      
      <Typography
        variant="caption"
        sx={{
          position: 'absolute',
          right: 6,
          top: '68%',
          fontSize: '5px',
          color: '#6B7280',
          fontWeight: 500,
          whiteSpace: 'nowrap',
          pointerEvents: 'none',
        }}
      >
        False
      </Typography>
    </Box>
  );
};

export default ConditionalRouteNode;
